import type { ReactNode } from 'react'
import { Sparkles } from 'lucide-react'
import { KiArbeitet } from '@/components/ki/KiArbeitet'
import { Abschnitt } from '@/lib/speichern'

/**
 * **Das Vorschlagsfenster der KI** (AP-22 Teil 6, V) — ein Baustein für jede Maske, in der die KI
 * etwas aus einem Original liest und der Mensch entscheidet: Eingangsrechnung (AP-34 N2),
 * Abgabezettel (AP-22-I), Positionen des zugeordneten Belegs im Posteingang (AP-39 Nachtrag 1).
 *
 * Gemeinsam sind Rahmen, Zustände, die gelbe Hinterlegung für unsicher Gelesenes samt Grund,
 * die Entscheidung je Zeile (übernommen · verworfen) und die Fußzeile. Was in den Zeilen steht,
 * gehört der jeweiligen Maske — sie baut ihre Tabelle aus diesen Teilen.
 *
 * In die Daten kommt erst, was übernommen wird; das Fenster selbst schreibt nichts.
 *
 * **Aussehen** (Auftraggeber 5.10.2026, zuerst an der Eingangsrechnung): eigene Farbe und
 * gestrichelter Rand — ein Vorschlag, nicht die Daten selbst. **Läuft die KI**, steht die
 * Beschäftigungsanzeige aus `components/ki/KiArbeitet` da (KA-52,
 * `.claude/rules/ki-beschaeftigungsanzeige.md`) — nie ein eigener Spinner oder stehender Text.
 */

/** Was an einer Zeile des Vorschlags entschieden ist — `null` heißt offen. */
export type KiEntscheidung = 'TAKEN' | 'DISCARDED' | null

/** Hinterlegung für unsicher Gelesenes — an der Zeile oder an der Zelle. */
export const kiGelb = 'bg-amber-500/15'
/** Zellen der Vorschlagstabelle. */
export const kiTh = 'px-3 py-1.5 font-medium'
export const kiTd = 'px-3 py-1.5'

/** Ein Nebenknopf im Fenster — auch für Handlungen der jeweiligen Maske. */
export const kiKnopf = 'inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50'
const knopf = kiKnopf
const haupt = 'inline-flex items-center gap-1 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50'

/** Die Schriftfarbe des Vorschlags — Titel, Marke und Beschäftigungsanzeige. */
export const kiFarbe = 'text-violet-700 dark:text-violet-300'

/**
 * Der Rahmen: ein Abschnitt in der Farbe des Vorschlags, darin die Zustände und der Vorschlag.
 * `marke` steht neben dem Titel, solange etwas zu entscheiden ist („noch nicht in der Rechnung").
 */
export function KiVorschlag({
  titel = 'KI-Vorschlag',
  marke,
  aktionen,
  children,
}: {
  titel?: ReactNode
  marke?: ReactNode
  aktionen?: ReactNode
  children: ReactNode
}) {
  return (
    <Abschnitt
      className="!border-2 !border-dashed !border-violet-500/50 !bg-violet-500/5"
      titel={
        <span className={`flex flex-wrap items-center gap-2 ${kiFarbe}`}>
          <Sparkles className="h-4 w-4" /> {titel}
          {marke && <span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-xs font-medium">{marke}</span>}
        </span>
      }
      aktionen={aktionen}
    >
      <div className="space-y-3">{children}</div>
    </Abschnitt>
  )
}

/**
 * Die KI liest gerade (KA-52): die Beschäftigungsanzeige mit dem Text der Stelle — ohne „…", die
 * Punkte sind die Auslassungspunkte —, daneben, was der Mensch inzwischen wissen muss.
 */
export function KiLiest({ text, children }: { text: string; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
      <KiArbeitet text={text} className={`font-medium ${kiFarbe}`} />
      {children && <span className="text-muted-foreground">{children}</span>}
    </div>
  )
}

/** „Mit KI lesen" bzw. „Erneut lesen". */
export function KiLesenKnopf({ erneut, disabled, onClick }: { erneut: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button type="button" className={knopf} disabled={disabled} onClick={onClick}>
      <Sparkles className="h-4 w-4" /> {erneut ? 'Erneut lesen' : 'Mit KI lesen'}
    </button>
  )
}

/** Eine Zustandszeile — keine Lesung, gescheitert, nicht lesbar, abgearbeitet — mit dem Knopf daneben. */
export function KiZustand({ fehler, aktion, children }: { fehler?: boolean; aktion?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <p className={`text-sm ${fehler ? 'text-destructive' : 'text-muted-foreground'}`}>{children}</p>
      {aktion}
    </div>
  )
}

/** Der Satz über dem Vorschlag, klein darunter `zusatz` (womit gelesen wurde) und die Hinweise der KI zum ganzen Blatt. */
export function KiEinleitung({ hinweise = [], zusatz, children }: { hinweise?: string[]; zusatz?: ReactNode; children: ReactNode }) {
  return (
    <>
      <p className="text-sm text-muted-foreground">{children}</p>
      {zusatz && <p className="text-xs text-muted-foreground">{zusatz}</p>}
      {hinweise.map((h) => (
        <p key={h} className="rounded-md bg-muted/50 px-3 py-1.5 text-sm">
          {h}
        </p>
      ))}
    </>
  )
}

/**
 * Die Tabelle des Vorschlags; `kopf` sind die `<th>` einer Kopfzeile, die Kinder die `<tr>`.
 * `dicht` für Vorschläge mit vielen Spalten (ein Monat Tageszeilen): kleinere Schrift, engere Zellen.
 */
export function KiTabelle({ kopf, dicht, children }: { kopf: ReactNode; dicht?: boolean; children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className={dicht ? 'w-full text-xs [&_td]:px-2 [&_td]:py-1 [&_th]:px-2 [&_th]:py-1' : 'w-full text-sm'}>
        <thead className="border-b border-border bg-muted/50 text-left">
          <tr>{kopf}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

/** Die Auswahlzelle am Zeilenanfang — leer, sobald entschieden ist oder nichts zu übernehmen wäre. */
export function KiAuswahl({
  label,
  zeigen,
  checked,
  onChange,
}: {
  label: string
  zeigen: boolean
  checked: boolean
  onChange: () => void
}) {
  return (
    <td className="w-8 px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
      {zeigen && <input type="checkbox" aria-label={label} checked={checked} onChange={onChange} />}
    </td>
  )
}

/** Die Zelle am Zeilenende: die Entscheidung, sonst — wer darf — „Verwerfen". */
export function KiEntscheidungZelle({
  entscheidung,
  darf,
  disabled,
  onVerwerfen,
  sonst,
  haftet,
}: {
  entscheidung: KiEntscheidung
  darf: boolean
  disabled?: boolean
  onVerwerfen: () => void
  /** Statt „Verwerfen", wo es nichts zu entscheiden gibt (z. B. „steht schon so da"). */
  sonst?: ReactNode
  /** Bleibt am rechten Rand stehen, wenn die Tabelle breiter ist als ihr Platz — die Entscheidung ist immer erreichbar. */
  haftet?: boolean
}) {
  return (
    <td className={`whitespace-nowrap px-3 py-1.5 text-right ${haftet ? 'sticky right-0 bg-card' : ''}`} onClick={(e) => e.stopPropagation()}>
      {entscheidung ? (
        <span className="text-xs text-muted-foreground">{entscheidung === 'TAKEN' ? 'übernommen' : 'verworfen'}</span>
      ) : sonst !== undefined ? (
        <span className="text-xs text-muted-foreground">{sonst}</span>
      ) : (
        darf && (
          <button type="button" className="text-xs text-muted-foreground underline hover:text-foreground" disabled={disabled} onClick={onVerwerfen}>
            Verwerfen
          </button>
        )
      )}
    </td>
  )
}

/**
 * Die Fußzeile: Übernehmen (n) · Alle sicheren auswählen · Rest verwerfen — rechts „Erneut lesen".
 * `nebenbei` steht hinter den Knöpfen (damit sie nicht springen): die Beschäftigungsanzeige einer
 * Aufgabe, die das Übernehmen nebenbei an die KI stellt.
 */
export function KiFuss({
  anzahl,
  laeuft,
  onUebernehmen,
  onSichere,
  onRestVerwerfen,
  nebenbei,
  lesen,
}: {
  /** Wie viel gerade angehakt ist. */
  anzahl: number
  laeuft: boolean
  onUebernehmen: () => void
  onSichere: () => void
  onRestVerwerfen: () => void
  nebenbei?: ReactNode
  lesen?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" className={haupt} disabled={laeuft || anzahl === 0} onClick={onUebernehmen}>
        Übernehmen{anzahl > 0 ? ` (${anzahl})` : ''}
      </button>
      <button type="button" className={knopf} disabled={laeuft} onClick={onSichere}>
        Alle sicheren auswählen
      </button>
      <button type="button" className={knopf} disabled={laeuft} onClick={onRestVerwerfen}>
        Rest verwerfen
      </button>
      {nebenbei}
      {lesen && <span className="ml-auto">{lesen}</span>}
    </div>
  )
}
